import React, { useState } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, Download, Loader2, Users, Play } from 'lucide-react';
import * as XLSX from 'xlsx';

const BatchUpload = () => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const BASE_URL = process.env.REACT_APP_API_URL;

  
  // State untuk Progressss
  const [progress, setProgress] = useState({ 
    total: 0, 
    processed: 0, 
    success: 0, 
    failed: 0, 
    percent: 0 
  });
  
  const [logs, setLogs] = useState([]); // Menyimpan detail error

const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Ambil data dari API Export yang baru kita buat
      const response = await fetch(`${BASE_URL}/api/users/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error("Gagal mengambil data dari server");
      }

      const existingData = await response.json();
      let dataToExport = [];

      // Jika database masih kosong, kasih dummy template
      if (existingData.length === 0) {
        dataToExport = [
          { USERNAME: "198001...", NAMA: "Guru A", ROLE: "guru", UNIT_KERJA: "SDN 01", KECAMATAN: "Cipayung", PASSWORD: "" }
        ];
      } else {
        // Jika ada data, tambahkan kolom password kosong di akhir
        dataToExport = existingData.map(user => ({
          ...user,
          PASSWORD: "" // Biarkan kosong, admin isi hanya jika mau reset password user (opsional jika kamu mau handle reset password via excel)
        }));
      }

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Data_User");
      XLSX.writeFile(wb, "Data_Batch_User.xlsx");
      
    } catch (error) {
      console.error(error);
      alert("Terjadi kesalahan saat mendownload template.");
    }
  };

  const processFile = async () => {
    if (!file) return alert("Pilih file Excel dulu!");
    
    setIsProcessing(true);
    setLogs([]);
    setProgress({ total: 0, processed: 0, success: 0, failed: 0, percent: 0 });

    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        // 1. Baca Excel di Browsert
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        const totalData = jsonData.length;
        if (totalData === 0) {
            alert("File Excel kosong!");
            setIsProcessing(false);
            return;
        }

        // Update State Awal
        setProgress(prev => ({ ...prev, total: totalData }));

        // 2. Pecah Data jadi Batch (misal 20 baris per request)
        const BATCH_SIZE = 500; // Sebelumnya 20
        let processedCount = 0;
        let successTotal = 0;
        let failTotal = 0;
        let errorLogs = [];

        const token = localStorage.getItem('token');

        // Loop Chunking
        for (let i = 0; i < totalData; i += BATCH_SIZE) {
            const batch = jsonData.slice(i, i + BATCH_SIZE);
            
            // Kirim ke Backend
            const res = await fetch(`${BASE_URL}/api/users/batch-insert-json`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(batch)
            });

            const result = await res.json();

            // Hitung Progress
            if (res.ok) {
                successTotal += result.success;
                failTotal += result.failed;
                if(result.errors.length > 0) {
                    errorLogs = [...errorLogs, ...result.errors];
                }
            } else {
                failTotal += batch.length; // Anggap gagal semua kalau server error
                errorLogs.push(`Batch ${i} gagal: Server Error`);
            }

            processedCount += batch.length;
            
            // Update UI Progress Real-time
            const percent = Math.round((processedCount / totalData) * 100);
            setProgress({
                total: totalData,
                processed: Math.min(processedCount, totalData),
                success: successTotal,
                failed: failTotal,
                percent: percent
            });
            
            // Simpan logs sementara agar bisa dilihat user realtime
            setLogs(errorLogs);
        }

      } catch (err) {
        console.error(err);
        alert("Gagal membaca file Excel.");
      } finally {
        setIsProcessing(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      <div className="mb-8 border-b border-slate-200 pb-4">
        <h1 className="text-3xl font-serif text-slate-900 flex items-center gap-3">
            <Users className="text-amber-600"/> 
            Batch User Upload
        </h1>
        <p className="text-slate-500 mt-1">Import data massal dengan indikator progres real-time.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        
        {/* Panel Kiri: Instruksi */}
        <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 h-fit">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <FileSpreadsheet size={20}/> Instruksi
          </h3>
        <ul className="text-sm text-slate-600 space-y-2 list-disc pl-5 mb-6">
          <li>Klik "Download Template" untuk mengambil data guru yang sudah ada di sistem.</li>
          <li>Kolom wajib: USERNAME, NAMA, ROLE.</li>
          <li>Jangan ubah USERNAME. Jika data sudah ada, sistem akan otomatis melakukan Update (Nama, Unit Kerja, Kecamatan).</li>
          <li>Jika menambah USERNAME baru, sistem akan menganggapnya sebagai guru baru.</li>
        </ul>
          <button onClick={downloadTemplate} className="w-full flex items-center justify-center gap-2 bg-white border border-slate-300 py-2 rounded-lg text-sm font-bold hover:bg-slate-100 transition">
            <Download size={16}/> Download Template
          </button>
        </div>

        {/* Panel Kanan: Input File */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
           <h3 className="font-bold text-slate-800 mb-4">Upload File</h3>
           
           <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:bg-slate-50 relative mb-4">
             <input 
               type="file" 
               accept=".xlsx, .xls"
               onChange={(e) => setFile(e.target.files[0])}
               disabled={isProcessing}
               className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
             />
             <div className="pointer-events-none">
                {file ? (
                    <div className="text-emerald-600 font-bold flex flex-col items-center">
                        <FileSpreadsheet size={32} className="mb-2"/>
                        <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                    </div>
                ) : (
                    <div className="text-slate-400 flex flex-col items-center">
                        <Upload size={32} className="mb-2"/>
                        <span className="text-sm">Pilih File Excel</span>
                    </div>
                )}
             </div>
           </div>

           <button 
             onClick={processFile}
             disabled={isProcessing || !file}
             className="w-full bg-slate-900 text-white py-3 rounded-lg font-bold hover:bg-slate-800 disabled:bg-slate-300 flex justify-center items-center gap-2 transition-all"
           >
             {isProcessing ? <Loader2 className="animate-spin" size={20}/> : <Play size={20}/>}
             {isProcessing ? "Sedang Memproses..." : "Mulai Proses Upload"}
           </button>
        </div>
      </div>

      {/* --- PROGRESS BAR SECTION --- */}
      {(isProcessing || progress.total > 0) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden animate-fade-in-up">
            
            {/* Header Status */}
            <div className="p-6 border-b border-slate-100">
                <div className="flex justify-between items-end mb-2">
                    <div>
                        <h3 className="font-bold text-slate-800 text-lg">
                            {isProcessing ? "Memproses Data..." : "Proses Selesai"}
                        </h3>
                        <p className="text-slate-500 text-sm">
                            Berhasil memasukkan <b>{progress.success}</b> dari <b>{progress.total}</b> data.
                        </p>
                    </div>
                    <span className="text-3xl font-bold text-slate-900">{progress.percent}%</span>
                </div>

                {/* Bar Visual */}
                <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden relative">
                    <div 
                        className="bg-emerald-500 h-full transition-all duration-500 ease-out"
                        style={{ width: `${progress.percent}%` }}
                    ></div>
                    {/* Efek Shimmer saat loading */}
                    {isProcessing && (
                        <div className="absolute inset-0 bg-white/30 w-full animate-pulse"></div>
                    )}
                </div>
            </div>

            {/* Statistik Grid */}
            <div className="grid grid-cols-2 divide-x divide-slate-100">
                <div className="p-4 flex items-center justify-center gap-3 bg-emerald-50/50">
                    <CheckCircle className="text-emerald-600" size={24}/>
                    <div className="text-center">
                        <div className="text-xs text-emerald-600 font-bold uppercase tracking-wider">Sukses</div>
                        <div className="text-xl font-bold text-emerald-800">{progress.success}</div>
                    </div>
                </div>
                <div className="p-4 flex items-center justify-center gap-3 bg-red-50/50">
                    <AlertCircle className="text-red-600" size={24}/>
                    <div className="text-center">
                        <div className="text-xs text-red-600 font-bold uppercase tracking-wider">Gagal</div>
                        <div className="text-xl font-bold text-red-800">{progress.failed}</div>
                    </div>
                </div>
            </div>

            {/* Log Error (Jika ada) */}
            {logs.length > 0 && (
                <div className="bg-slate-900 text-slate-300 p-4 text-xs font-mono max-h-40 overflow-y-auto">
                    <div className="mb-2 font-bold text-slate-100 sticky top-0 bg-slate-900 pb-1 border-b border-slate-700">
                        Error Log ({logs.length}):
                    </div>
                    <ul className="space-y-1">
                        {logs.map((log, idx) => (
                            <li key={idx} className="flex gap-2 text-red-400">
                                <span>[FAIL]</span> {log}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
      )}

    </div>
  );
};

export default BatchUpload;